import { useState, useCallback } from "react";
import { Plus, Trash, Table, Graph } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { StudyInput, StudySelect, StudyResultCard, StudyLabel, useStudyRunner, TemplateDropdown } from "./StudyUI";
import SLDEditor from "./SLDEditor";
import ShortCircuitSLD from "./ShortCircuitSLD";
import MotorStartSLD from "./MotorStartSLD";
import TCCSLD from "./TCCSLD";
import ReliabilitySLD from "./ReliabilitySLD";
import CableAmpacitySLD from "./CableAmpacitySLD";
import MultiMachineSLD from "./MultiMachineSLD";
import HarmonicsSpectrum from "./HarmonicsSpectrum";
import BatteryTimeline from "./BatteryTimeline";
import GroundingGrid from "./GroundingGrid";

/* Shared: diagram|table mode toggle used by every card that has an
   accompanying graphical SLD editor. Pass a custom `options` array to
   reuse this as a generic two-way switch (see OpfCard: lossless / with_losses). */
function ModeToggle({ mode, setMode, prefix = "lf-mode", testid, options }) {
  const rootId = testid || `${prefix}-toggle`;
  const opts = options || [
    { value: "diagram", label: "diagram", icon: Graph },
    { value: "table",   label: "table",   icon: Table },
  ];
  return (
    <div className="border border-white/10 flex text-[10px] font-mono" data-testid={rootId}>
      {opts.map((o) => {
        const Icon = o.icon;
        const on = mode === o.value;
        return (
          <button key={o.value} onClick={() => setMode(o.value)}
            className={`px-3 py-1.5 flex items-center gap-1 transition ${on ? "bg-[#00E5FF] text-black" : "bg-transparent text-gray-400 hover:text-white"}`}
            data-testid={`${prefix}-${o.value}`}>
            {Icon && <Icon size={12} weight={on ? "fill" : "regular"} />} {o.label}
          </button>
        );
      })}
    </div>
  );
}

/* v2.92 · The seven study cards. Each is self-contained so future
   graphical-SLD upgrades to a single study can't ripple to the rest. */

/* ── 1. Load Flow ─────────────────────────────────────────────── */
export function LoadFlowCard() {
  const [mode, setMode] = useState("diagram");     // v2.93 · "table" | "diagram"
  const [buses, setBuses] = useState([
    { id: "B1", kV: 13.8, type: "SLACK", V_pu: 1.02, P_mw: 0, Q_mvar: 0 },
    { id: "B2", kV: 13.8, type: "PQ",    V_pu: 1.0,  P_mw: 30, Q_mvar: 15 },
    { id: "B3", kV: 13.8, type: "PQ",    V_pu: 1.0,  P_mw: 20, Q_mvar: 10 },
  ]);
  const [lines, setLines] = useState([
    { from_bus: "B1", to_bus: "B2", R_pu: 0.01, X_pu: 0.05, B_pu: 0 },
    { from_bus: "B2", to_bus: "B3", R_pu: 0.02, X_pu: 0.08, B_pu: 0 },
  ]);
  const [baseMva, setBaseMva] = useState(100);
  const { result, error, run, Button, payload, kind } = useStudyRunner("/studies/load-flow", "lf-run");
  const studyName = "Load flow";

  const patchBus = (i, patch) => { const l = [...buses]; l[i] = { ...l[i], ...patch }; setBuses(l); };
  const patchLine = (i, patch) => { const l = [...lines]; l[i] = { ...l[i], ...patch }; setLines(l); };

  // v2.93 · Stable callback so the SLD editor's useEffect doesn't fire in a
  // render loop when parent re-renders.
  const onSldChange = useCallback(({ buses: b, lines: l }) => {
    setBuses(b); setLines(l);
  }, []);

  return (
    <div data-testid="study-load-flow">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-display text-2xl font-bold">Load flow · Gauss-Seidel AC</h3>
          <p className="text-sm text-gray-400 mt-1">One SLACK bus required. Others are PQ (load/gen) or PV.</p>
        </div>
        {/* v2.93 · Diagram ↔ Table mode toggle */}
        <ModeToggle mode={mode} setMode={setMode} prefix="lf-mode" />
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <StudyInput label="Base MVA" type="number" value={baseMva} onChange={(e) => setBaseMva(Number(e.target.value))} data-testid="lf-base" />
      </div>

      {mode === "diagram" ? (
        <div className="mt-5">
          {/* key on bus-id signature so template loads redraw the canvas */}
          <SLDEditor key={buses.map((b) => b.id).join("|")}
                     initialBuses={buses} initialLines={lines}
                     onChange={onSldChange} />
          <div className="mt-2 text-[10px] font-mono text-gray-500">
            Diagram is authoritative in this mode — switch to <span className="text-[#00E5FF]">table</span> to hand-edit the bus / line rows.
          </div>
        </div>
      ) : (
        <>
          <div className="mt-5">
            <div className="flex items-center justify-between mb-1">
              <StudyLabel text="Buses" />
              <button onClick={() => setBuses([...buses, { id: `B${buses.length+1}`, kV: 13.8, type: "PQ", V_pu: 1.0, P_mw: 0, Q_mvar: 0 }])} className="text-xs text-[#00E5FF] hover:underline"><Plus size={12} className="inline" /> add bus</button>
            </div>
            {buses.map((b, i) => (
              <div key={i} className="grid grid-cols-12 gap-1 mb-1" data-testid={`lf-bus-${i}`}>
                <input value={b.id} onChange={(e) => patchBus(i, { id: e.target.value })} className="col-span-2 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono" placeholder="id" />
                <input type="number" value={b.kV} onChange={(e) => patchBus(i, { kV: Number(e.target.value) })} className="col-span-2 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono" placeholder="kV" />
                <select value={b.type} onChange={(e) => patchBus(i, { type: e.target.value })} className="col-span-2 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono">
                  <option>SLACK</option><option>PQ</option><option>PV</option>
                </select>
                <input type="number" step="0.01" value={b.V_pu} onChange={(e) => patchBus(i, { V_pu: Number(e.target.value) })} className="col-span-2 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono" placeholder="V pu" />
                <input type="number" value={b.P_mw} onChange={(e) => patchBus(i, { P_mw: Number(e.target.value) })} className="col-span-2 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono" placeholder="P MW" />
                <input type="number" value={b.Q_mvar} onChange={(e) => patchBus(i, { Q_mvar: Number(e.target.value) })} className="col-span-1 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono" placeholder="Q MVAr" />
                <button onClick={() => setBuses(buses.filter((_, x) => x !== i))} className="col-span-1 text-[#FF3B30] text-xs"><Trash size={12} /></button>
              </div>
            ))}
          </div>

          <div className="mt-5">
            <div className="flex items-center justify-between mb-1">
              <StudyLabel text="Lines (impedance in p.u. on system base)" />
              <button onClick={() => setLines([...lines, { from_bus: "", to_bus: "", R_pu: 0.01, X_pu: 0.05, B_pu: 0 }])} className="text-xs text-[#00E5FF] hover:underline"><Plus size={12} className="inline" /> add line</button>
            </div>
            {lines.map((l, i) => (
              <div key={i} className="grid grid-cols-12 gap-1 mb-1" data-testid={`lf-line-${i}`}>
                <input value={l.from_bus} onChange={(e) => patchLine(i, { from_bus: e.target.value })} className="col-span-3 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono" placeholder="from" />
                <input value={l.to_bus} onChange={(e) => patchLine(i, { to_bus: e.target.value })} className="col-span-3 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono" placeholder="to" />
                <input type="number" step="0.001" value={l.R_pu} onChange={(e) => patchLine(i, { R_pu: Number(e.target.value) })} className="col-span-2 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono" placeholder="R pu" />
                <input type="number" step="0.001" value={l.X_pu} onChange={(e) => patchLine(i, { X_pu: Number(e.target.value) })} className="col-span-2 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono" placeholder="X pu" />
                <input type="number" step="0.001" value={l.B_pu} onChange={(e) => patchLine(i, { B_pu: Number(e.target.value) })} className="col-span-1 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono" placeholder="B pu" />
                <button onClick={() => setLines(lines.filter((_, x) => x !== i))} className="col-span-1 text-[#FF3B30] text-xs"><Trash size={12} /></button>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="mt-5 flex items-center gap-2 flex-wrap">
        <Button onClick={() => run({ base_mva: baseMva, buses, lines })} />
        <TemplateDropdown kind="load_flow" onLoad={(inputs) => { setBaseMva(inputs.base_mva ?? 100); setBuses(inputs.buses || []); setLines(inputs.lines || []); }} />
      </div>
      <StudyResultCard result={result} error={error} kind={kind} payload={payload} name={studyName} />
    </div>
  );
}

/* ── 2. Short-Circuit ─────────────────────────────────────────── */
export function ShortCircuitCard() {
  const [mode, setMode] = useState("diagram");    // v2.94 · diagram|table
  const [form, setForm] = useState({ voltage_kv: 13.8, source_sc_mva: 250, x_over_r: 10,
    transformer_kva: 0, transformer_impedance_pct: 5.75, fault_type: "3P" });
  const [motors, setMotors] = useState([]);
  const [diagramPayload, setDiagramPayload] = useState(null);
  const { result, error, run, Button, payload, kind } = useStudyRunner("/studies/short-circuit", "sc-run");
  const studyName = "Short-circuit";
  const patch = (p) => setForm({ ...form, ...p });
  const patchMotor = (i, p) => { const l = [...motors]; l[i] = { ...l[i], ...p }; setMotors(l); };
  const onSldChange = useCallback((p) => setDiagramPayload(p), []);
  return (
    <div data-testid="study-short-circuit">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-display text-2xl font-bold">Short-circuit · IEC 60909 + motor contribution</h3>
          <p className="text-sm text-gray-400 mt-1">
            Multi-source model — utility + optional transformer + induction/synchronous motors + generators. Returns ½-cycle
            momentary (all machines at X&quot;d), 1.5-4-cycle interrupting (induction reduced by ANSI 1.2×), and 30-cycle
            steady state (motors decayed). ETAP-comparable.
          </p>
        </div>
        <ModeToggle mode={mode} setMode={setMode} prefix="sc-mode" />
      </div>

      {mode === "diagram" ? (
        <div className="mt-5">
          <ShortCircuitSLD onChange={onSldChange} />
          <div className="mt-2 text-[10px] font-mono text-gray-500">
            Diagram is authoritative — switch to <span className="text-[#00E5FF]">table</span> to hand-edit the utility / transformer / motors.
          </div>
        </div>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
            <StudyInput label="Voltage (kV L-L)" type="number" value={form.voltage_kv} onChange={(e) => patch({ voltage_kv: Number(e.target.value) })} data-testid="sc-kv" />
            <StudyInput label="Source SC MVA" type="number" value={form.source_sc_mva} onChange={(e) => patch({ source_sc_mva: Number(e.target.value) })} data-testid="sc-mva" />
            <StudyInput label="Source X / R" type="number" step="0.1" value={form.x_over_r} onChange={(e) => patch({ x_over_r: Number(e.target.value) })} />
            <StudyInput label="Transformer kVA (0 = skip)" type="number" value={form.transformer_kva} onChange={(e) => patch({ transformer_kva: Number(e.target.value) })} />
            <StudyInput label="Tx impedance %" type="number" step="0.01" value={form.transformer_impedance_pct} onChange={(e) => patch({ transformer_impedance_pct: Number(e.target.value) })} />
            <StudySelect label="Fault type" value={form.fault_type} onChange={(e) => patch({ fault_type: e.target.value })}
              options={[{value:"3P",label:"3-phase"},{value:"LG",label:"L-G"},{value:"LL",label:"L-L"}]} data-testid="sc-ft" />
          </div>

          <div className="mt-5">
            <div className="flex items-center justify-between mb-1">
              <StudyLabel text="Motors + generators (per ANSI C37.010)" />
              <button onClick={() => setMotors([...motors, { tag: `M${motors.length+1}`, rated_kva: 500, x_subtransient_pu: 0.17, kind: "induction", x_over_r: 6 }])}
                className="text-xs text-[#00E5FF] hover:underline"><Plus size={12} className="inline" /> add machine</button>
            </div>
            {motors.map((m, i) => (
              <div key={i} className="grid grid-cols-12 gap-1 mb-1" data-testid={`sc-motor-${i}`}>
                <input value={m.tag} onChange={(e) => patchMotor(i, { tag: e.target.value })} className="col-span-3 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono" placeholder="tag" />
                <input type="number" value={m.rated_kva} onChange={(e) => patchMotor(i, { rated_kva: Number(e.target.value) })} className="col-span-2 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono" placeholder="kVA" />
                <input type="number" step="0.01" value={m.x_subtransient_pu} onChange={(e) => patchMotor(i, { x_subtransient_pu: Number(e.target.value) })} className="col-span-2 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono" placeholder='X"d pu' />
                <select value={m.kind} onChange={(e) => patchMotor(i, { kind: e.target.value })} className="col-span-3 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono">
                  <option value="induction">induction motor</option>
                  <option value="synchronous">synchronous motor</option>
                  <option value="generator">generator</option>
                </select>
                <input type="number" step="0.5" value={m.x_over_r} onChange={(e) => patchMotor(i, { x_over_r: Number(e.target.value) })} className="col-span-1 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono" placeholder="X/R" />
                <button onClick={() => setMotors(motors.filter((_, x) => x !== i))} className="col-span-1 text-[#FF3B30] text-xs"><Trash size={12} /></button>
              </div>
            ))}
            {motors.length === 0 && <div className="text-[10px] text-gray-500 font-mono">No machines — computes utility-only baseline.</div>}
          </div>
        </>
      )}

      <div className="mt-5 flex items-center gap-2 flex-wrap">
        <Button onClick={() => run(mode === "diagram" ? diagramPayload : { ...form, motors })} />
        <TemplateDropdown kind="short_circuit" onLoad={(inputs) => { const { motors: m = [], ...rest } = inputs; setForm({ ...form, ...rest }); setMotors(m); }} />
      </div>
      <StudyResultCard result={result} error={error} kind={kind} payload={payload} name={studyName} />
    </div>
  );
}

/* ── 3. Motor Starting ────────────────────────────────────────── */
export function MotorStartingCard() {
  const [mode, setMode] = useState("diagram");    // v2.94 · diagram|table
  const [form, setForm] = useState({ system_voltage_kv: 0.48, system_sc_mva: 30, motor_hp: 500,
    motor_pf_locked_rotor: 0.3, motor_locked_rotor_kva_per_hp: 6.0, starting_method: "DOL" });
  const [diagramPayload, setDiagramPayload] = useState(null);
  const { result, error, run, Button, payload, kind } = useStudyRunner("/studies/motor-starting", "ms-run");
  const studyName = "Motor starting";
  const patch = (p) => setForm({ ...form, ...p });
  const onSldChange = useCallback((p) => setDiagramPayload(p), []);
  return (
    <div data-testid="study-motor-starting">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-display text-2xl font-bold">Motor starting · voltage dip</h3>
          <p className="text-sm text-gray-400 mt-1">V-dip and starting torque during motor in-rush.</p>
        </div>
        <ModeToggle mode={mode} setMode={setMode} prefix="ms-mode" />
      </div>

      {mode === "diagram" ? (
        <div className="mt-5">
          <MotorStartSLD onChange={onSldChange} />
          <div className="mt-2 text-[10px] font-mono text-gray-500">
            Diagram is authoritative — switch to <span className="text-[#00E5FF]">table</span> for spreadsheet input.
          </div>
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
          <StudyInput label="System V (kV)" type="number" step="0.01" value={form.system_voltage_kv} onChange={(e) => patch({ system_voltage_kv: Number(e.target.value) })} />
          <StudyInput label="System SC MVA (before start)" type="number" value={form.system_sc_mva} onChange={(e) => patch({ system_sc_mva: Number(e.target.value) })} data-testid="ms-scmva" />
          <StudyInput label="Motor HP" type="number" value={form.motor_hp} onChange={(e) => patch({ motor_hp: Number(e.target.value) })} data-testid="ms-hp" />
          <StudyInput label="LRA (kVA per HP)" type="number" step="0.1" value={form.motor_locked_rotor_kva_per_hp} onChange={(e) => patch({ motor_locked_rotor_kva_per_hp: Number(e.target.value) })} />
          <StudySelect label="Starting method" value={form.starting_method} onChange={(e) => patch({ starting_method: e.target.value })}
            options={["DOL","STAR_DELTA","SOFT","VFD"]} data-testid="ms-method" />
        </div>
      )}

      <div className="mt-5 flex items-center gap-2 flex-wrap">
        <Button onClick={() => run(mode === "diagram" ? diagramPayload : form)} />
        <TemplateDropdown kind="motor_starting" onLoad={(inputs) => setForm({ ...form, ...inputs })} />
      </div>
      <StudyResultCard result={result} error={error} kind={kind} payload={payload} name={studyName} />
    </div>
  );
}

/* ── 4. TCC Coordination ──────────────────────────────────────── */
export function TCCCard() {
  const [mode, setMode] = useState("diagram");                        // v2.95
  const [devices, setDevices] = useState([
    { tag: "51-DS", pickup_amps: 100, time_dial: 2, curve: "MI" },
    { tag: "51-US", pickup_amps: 200, time_dial: 5, curve: "MI" },
  ]);
  const [fault, setFault] = useState(2000);
  const [gap, setGap] = useState(0.3);
  const [diagramPayload, setDiagramPayload] = useState(null);
  const { result, error, run, Button, payload, kind } = useStudyRunner("/studies/tcc", "tcc-run");
  const studyName = "TCC coordination";
  const patch = (i, p) => { const l = [...devices]; l[i] = { ...l[i], ...p }; setDevices(l); };
  const onSldChange = useCallback((p) => setDiagramPayload(p), []);
  return (
    <div data-testid="study-tcc">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-display text-2xl font-bold">TCC coordination · IEEE C37.112</h3>
          <p className="text-sm text-gray-400 mt-1">Inverse-time overcurrent relay curves. List devices downstream → upstream.</p>
        </div>
        <ModeToggle mode={mode} setMode={setMode} prefix="tcc-mode" />
      </div>

      {mode === "diagram" ? (
        <div className="mt-5">
          <TCCSLD onChange={onSldChange} />
          <div className="mt-2 text-[10px] font-mono text-gray-500">
            Diagram is authoritative — devices are ordered from top (downstream) to bottom.
          </div>
        </div>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
            <StudyInput label="Fault current (A)" type="number" value={fault} onChange={(e) => setFault(Number(e.target.value))} data-testid="tcc-fault" />
            <StudyInput label="Min coord gap (s)" type="number" step="0.05" value={gap} onChange={(e) => setGap(Number(e.target.value))} />
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between mb-1">
              <StudyLabel text="Devices (downstream first)" />
              <button onClick={() => setDevices([...devices, { tag: `Rly-${devices.length+1}`, pickup_amps: 100, time_dial: 3, curve: "MI" }])} className="text-xs text-[#00E5FF] hover:underline"><Plus size={12} className="inline" /> add</button>
            </div>
            {devices.map((d, i) => (
              <div key={i} className="grid grid-cols-12 gap-1 mb-1" data-testid={`tcc-dev-${i}`}>
                <input value={d.tag} onChange={(e) => patch(i, { tag: e.target.value })} className="col-span-4 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono" placeholder="tag" />
                <input type="number" value={d.pickup_amps} onChange={(e) => patch(i, { pickup_amps: Number(e.target.value) })} className="col-span-3 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono" placeholder="pickup A" />
                <input type="number" step="0.1" value={d.time_dial} onChange={(e) => patch(i, { time_dial: Number(e.target.value) })} className="col-span-2 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono" placeholder="TD" />
                <select value={d.curve} onChange={(e) => patch(i, { curve: e.target.value })} className="col-span-2 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono">
                  <option>MI</option><option>VI</option><option>EI</option>
                </select>
                <button onClick={() => setDevices(devices.filter((_, x) => x !== i))} className="col-span-1 text-[#FF3B30] text-xs"><Trash size={12} /></button>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="mt-5 flex items-center gap-2 flex-wrap">
        <Button onClick={() => run(mode === "diagram" ? diagramPayload : { devices, fault_current_amps: fault, check_coordination_seconds: gap })} />
        <TemplateDropdown kind="tcc" onLoad={(inputs) => { setDevices(inputs.devices || []); setFault(inputs.fault_current_amps ?? 2000); setGap(inputs.check_coordination_seconds ?? 0.3); }} />
      </div>
      <StudyResultCard result={result} error={error} kind={kind} payload={payload} name={studyName} />
    </div>
  );
}

/* ── 5. Harmonics ─────────────────────────────────────────────── */
export function HarmonicsCard() {
  const [mode, setMode] = useState("diagram");                         // v2.95
  const [voltage, setVoltage] = useState(13.8);
  const [I1, setI1] = useState(1000);
  const [Isc, setIsc] = useState(25000);
  const [harmonics, setHarmonics] = useState([
    { h: 5, magnitude_pu: 0.038 }, { h: 7, magnitude_pu: 0.025 },
    { h: 11, magnitude_pu: 0.015 }, { h: 13, magnitude_pu: 0.010 },
  ]);
  const [diagramPayload, setDiagramPayload] = useState(null);
  const { result, error, run, Button, payload, kind } = useStudyRunner("/studies/harmonics", "hm-run");
  const studyName = "Harmonics";
  const patchRow = (i, p) => { const l = [...harmonics]; l[i] = { ...l[i], ...p }; setHarmonics(l); };
  const onSldChange = useCallback((p) => setDiagramPayload(p), []);
  return (
    <div data-testid="study-harmonics">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-display text-2xl font-bold">Harmonics · IEEE 519-2022</h3>
          <p className="text-sm text-gray-400 mt-1">Voltage THD + per-harmonic + TDD check at PCC.</p>
        </div>
        <ModeToggle mode={mode} setMode={setMode} prefix="hm-mode" />
      </div>

      {mode === "diagram" ? (
        <div className="mt-5">
          <HarmonicsSpectrum onChange={onSldChange} />
          <div className="mt-2 text-[10px] font-mono text-gray-500">
            Drag bar tops to change magnitude · click a bar to edit its order.
          </div>
        </div>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
            <StudyInput label="Voltage (kV L-L)" type="number" step="0.1" value={voltage} onChange={(e) => setVoltage(Number(e.target.value))} data-testid="hm-kv" />
            <StudyInput label="Fundamental current (A)" type="number" value={I1} onChange={(e) => setI1(Number(e.target.value))} />
            <StudyInput label="Isc at PCC (A)" type="number" value={Isc} onChange={(e) => setIsc(Number(e.target.value))} />
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between mb-1">
              <StudyLabel text="Harmonic spectrum (magnitude p.u. of fundamental)" />
              <button onClick={() => setHarmonics([...harmonics, { h: 17, magnitude_pu: 0.005 }])} className="text-xs text-[#00E5FF] hover:underline"><Plus size={12} className="inline" /> add</button>
            </div>
            {harmonics.map((h, i) => (
              <div key={i} className="grid grid-cols-12 gap-1 mb-1" data-testid={`hm-row-${i}`}>
                <input type="number" min={2} value={h.h} onChange={(e) => patchRow(i, { h: Number(e.target.value) })} className="col-span-3 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono" placeholder="order" />
                <input type="number" step="0.001" value={h.magnitude_pu} onChange={(e) => patchRow(i, { magnitude_pu: Number(e.target.value) })} className="col-span-8 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono" placeholder="pu" />
                <button onClick={() => setHarmonics(harmonics.filter((_, x) => x !== i))} className="col-span-1 text-[#FF3B30] text-xs"><Trash size={12} /></button>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="mt-5 flex items-center gap-2 flex-wrap">
        <Button onClick={() => run(mode === "diagram" ? diagramPayload : { voltage_kv: voltage, fundamental_current_a: I1, Isc_a: Isc, harmonics })} />
        <TemplateDropdown kind="harmonics" onLoad={(inputs) => { setVoltage(inputs.voltage_kv ?? 13.8); setI1(inputs.fundamental_current_a ?? 1000); setIsc(inputs.Isc_a ?? 25000); setHarmonics(inputs.harmonics || []); }} />
      </div>
      <StudyResultCard result={result} error={error} kind={kind} payload={payload} name={studyName} />
    </div>
  );
}

/* ── 6. Battery Sizing ────────────────────────────────────────── */
export function BatterySizingCard() {
  const [mode, setMode] = useState("diagram");                         // v2.95
  const [steps, setSteps] = useState([
    { load_amps: 50, duration_min: 60 },
    { load_amps: 150, duration_min: 5 },
  ]);
  const [meta, setMeta] = useState({ design_margin_pct: 15, aging_factor: 1.25,
    temp_correction_factor: 1.11, end_voltage_v_per_cell: 1.75, cells_in_series: 60 });
  const [diagramPayload, setDiagramPayload] = useState(null);
  const { result, error, run, Button, payload, kind } = useStudyRunner("/studies/battery-sizing", "bat-run");
  const studyName = "Battery sizing";
  const patchRow = (i, p) => { const l = [...steps]; l[i] = { ...l[i], ...p }; setSteps(l); };
  const onSldChange = useCallback((p) => setDiagramPayload(p), []);
  return (
    <div data-testid="study-battery">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-display text-2xl font-bold">Battery sizing · IEEE 485</h3>
          <p className="text-sm text-gray-400 mt-1">Duty-cycle section-by-section method.</p>
        </div>
        <ModeToggle mode={mode} setMode={setMode} prefix="bat-mode" />
      </div>

      {mode === "diagram" ? (
        <div className="mt-5">
          <BatteryTimeline onChange={onSldChange} />
          <div className="mt-2 text-[10px] font-mono text-gray-500">
            Drag bar heights for load, drag right edge for duration.
          </div>
        </div>
      ) : (
        <>
          <div className="mt-4">
            <div className="flex items-center justify-between mb-1">
              <StudyLabel text="Duty cycle steps" />
              <button onClick={() => setSteps([...steps, { load_amps: 50, duration_min: 30 }])} className="text-xs text-[#00E5FF] hover:underline"><Plus size={12} className="inline" /> add step</button>
            </div>
            {steps.map((s, i) => (
              <div key={i} className="grid grid-cols-12 gap-1 mb-1" data-testid={`bat-row-${i}`}>
                <input type="number" value={s.load_amps} onChange={(e) => patchRow(i, { load_amps: Number(e.target.value) })} className="col-span-5 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono" placeholder="Amps" />
                <input type="number" value={s.duration_min} onChange={(e) => patchRow(i, { duration_min: Number(e.target.value) })} className="col-span-6 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono" placeholder="Minutes" />
                <button onClick={() => setSteps(steps.filter((_, x) => x !== i))} className="col-span-1 text-[#FF3B30] text-xs"><Trash size={12} /></button>
              </div>
            ))}
          </div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
            <StudyInput label="Design margin %" type="number" value={meta.design_margin_pct} onChange={(e) => setMeta({ ...meta, design_margin_pct: Number(e.target.value) })} />
            <StudyInput label="Aging factor" type="number" step="0.01" value={meta.aging_factor} onChange={(e) => setMeta({ ...meta, aging_factor: Number(e.target.value) })} />
            <StudyInput label="Temp correction" type="number" step="0.01" value={meta.temp_correction_factor} onChange={(e) => setMeta({ ...meta, temp_correction_factor: Number(e.target.value) })} />
            <StudyInput label="Cells in series" type="number" value={meta.cells_in_series} onChange={(e) => setMeta({ ...meta, cells_in_series: Number(e.target.value) })} />
            <StudyInput label="End V per cell" type="number" step="0.01" value={meta.end_voltage_v_per_cell} onChange={(e) => setMeta({ ...meta, end_voltage_v_per_cell: Number(e.target.value) })} />
          </div>
        </>
      )}

      <div className="mt-5 flex items-center gap-2 flex-wrap">
        <Button onClick={() => run(mode === "diagram" ? diagramPayload : { steps, ...meta })} />
        <TemplateDropdown kind="battery_sizing" onLoad={(inputs) => { const { steps: s = [], ...rest } = inputs; setSteps(s); setMeta({ ...meta, ...rest }); }} />
      </div>
      <StudyResultCard result={result} error={error} kind={kind} payload={payload} name={studyName} />
    </div>
  );
}

/* ── 7. Grounding Grid ────────────────────────────────────────── */
export function GroundingCard() {
  const [mode, setMode] = useState("diagram");                         // v2.95
  const [form, setForm] = useState({ soil_resistivity_ohm_m: 100, grid_length_m: 600,
    grid_area_m2: 900, grid_depth_m: 0.5, conductor_diameter_mm: 10,
    fault_current_ka: 20, fault_clearing_s: 0.5, body_weight_kg: 70 });
  const [diagramPayload, setDiagramPayload] = useState(null);
  const { result, error, run, Button, payload, kind } = useStudyRunner("/studies/grounding", "gnd-run");
  const studyName = "Grounding";
  const patch = (p) => setForm({ ...form, ...p });
  const onSldChange = useCallback((p) => setDiagramPayload(p), []);
  return (
    <div data-testid="study-grounding">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-display text-2xl font-bold">Grounding grid · IEEE 80</h3>
          <p className="text-sm text-gray-400 mt-1">Sverak Rg + GPR + allowable touch/step voltages.</p>
        </div>
        <ModeToggle mode={mode} setMode={setMode} prefix="gnd-mode" />
      </div>

      {mode === "diagram" ? (
        <div className="mt-5">
          <GroundingGrid onChange={onSldChange} />
          <div className="mt-2 text-[10px] font-mono text-gray-500">
            Drag the yellow SE handle to resize the (square) grid footprint.
          </div>
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
          <StudyInput label="Soil resistivity (Ω·m)" type="number" value={form.soil_resistivity_ohm_m} onChange={(e) => patch({ soil_resistivity_ohm_m: Number(e.target.value) })} data-testid="gnd-rho" />
          <StudyInput label="Grid length L (m)" type="number" value={form.grid_length_m} onChange={(e) => patch({ grid_length_m: Number(e.target.value) })} />
          <StudyInput label="Grid area A (m²)" type="number" value={form.grid_area_m2} onChange={(e) => patch({ grid_area_m2: Number(e.target.value) })} />
          <StudyInput label="Grid depth h (m)" type="number" step="0.1" value={form.grid_depth_m} onChange={(e) => patch({ grid_depth_m: Number(e.target.value) })} />
          <StudyInput label="Fault current (kA)" type="number" value={form.fault_current_ka} onChange={(e) => patch({ fault_current_ka: Number(e.target.value) })} />
          <StudyInput label="Fault clearing (s)" type="number" step="0.05" value={form.fault_clearing_s} onChange={(e) => patch({ fault_clearing_s: Number(e.target.value) })} />
          <StudySelect label="Body weight kg" value={form.body_weight_kg} onChange={(e) => patch({ body_weight_kg: Number(e.target.value) })}
            options={[{value:50,label:"50 (conservative)"},{value:70,label:"70"}]} />
        </div>
      )}

      <div className="mt-5 flex items-center gap-2 flex-wrap">
        <Button onClick={() => run(mode === "diagram" ? diagramPayload : form)} />
        <TemplateDropdown kind="grounding" onLoad={(inputs) => setForm({ ...form, ...inputs })} />
      </div>
      <StudyResultCard result={result} error={error} kind={kind} payload={payload} name={studyName} />
    </div>
  );
}

/* v2.98 · P3 · ETAP modules #8-11 · table-mode only. Same shape as
   the other cards (useStudyRunner + StudyResultCard) so save-to-
   project + PDF export come for free. */

/* v2.99 · Inline swing-curve chart — renders δ(t) once TS returns a
   trace. Marks the fault-clearing instant + a 90°/180° stability
   guide. Zero external deps — pure SVG. */
function SwingCurve({ result, tc }) {
  const trace = result?.trace || [];
  if (!trace.length) return null;
  const w = 700, h = 200, pad = 32;
  const times = trace.map((p) => p.t_s);
  const deltas = trace.map((p) => p.delta_deg);
  const tMax = Math.max(...times, tc || 0.5);
  const dMax = Math.max(180, ...deltas);
  const dMin = Math.min(-30, ...deltas);
  const xFor = (t) => pad + (t / tMax) * (w - pad - 20);
  const yFor = (d) => h - pad - ((d - dMin) / (dMax - dMin)) * (h - pad - 20);
  const path = trace.map((p, i) => `${i ? "L" : "M"}${xFor(p.t_s).toFixed(1)},${yFor(p.delta_deg).toFixed(1)}`).join(" ");
  return (
    <div className="mt-3 border border-white/10 bg-[#0A0C10] p-2" data-testid="ts-swing-curve">
      <div className="text-[10px] font-mono text-gray-500 mb-1">// rotor angle δ(t)</div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full block">
        {[0, 90, 180].map((deg) => (
          <g key={deg}>
            <line x1={pad} x2={w - 20} y1={yFor(deg)} y2={yFor(deg)}
                  stroke={deg === 180 ? "#FF3B30" : "#ffffff10"} strokeDasharray={deg === 180 ? "3,3" : ""} />
            <text x={pad - 4} y={yFor(deg) + 3} textAnchor="end"
                  fill={deg === 180 ? "#FF3B30" : "#8792A2"} fontFamily="monospace" fontSize="9">{deg}°</text>
          </g>
        ))}
        {tc && (
          <>
            <line x1={xFor(tc)} x2={xFor(tc)} y1={pad} y2={h - pad}
                  stroke="#FFCC00" strokeDasharray="2,3" />
            <text x={xFor(tc) + 3} y={pad + 10} fill="#FFCC00"
                  fontFamily="monospace" fontSize="9">clear @ {tc.toFixed(3)}s</text>
          </>
        )}
        <path d={path} fill="none" stroke={result.stable ? "#7CFF6B" : "#FF3B30"} strokeWidth="1.8" />
        <text x={w / 2} y={h - 6} textAnchor="middle"
              fill="#8792A2" fontFamily="monospace" fontSize="10">time (s)</text>
        <text x={w - 20} y={yFor(dMin) - 4} textAnchor="end"
              fill={result.stable ? "#7CFF6B" : "#FF3B30"} fontFamily="monospace" fontSize="10">
          {result.stable ? "STABLE" : "UNSTABLE"} · δmax {result.max_delta_deg?.toFixed(1)}°
        </text>
      </svg>
    </div>
  );
}

/* v2.99 · Merit-order stack for OPF results. Each generator drawn as
   a bar in cost-ascending order; height = dispatched MW; colour
   yellow/green/red for cheap/mid/expensive by percentile. */
function MeritStack({ result }) {
  const gens = result?.generators || [];
  if (!gens.length) return null;
  const w = 700, h = 200, pad = 32;
  const sorted = [...gens].sort((a, b) => a.cost_per_hr / (a.p_mw || 1) - b.cost_per_hr / (b.p_mw || 1));
  const maxP = Math.max(...sorted.map((g) => g.p_mw));
  const barW = (w - pad - 20) / Math.max(1, sorted.length);
  return (
    <div className="mt-3 border border-white/10 bg-[#0A0C10] p-2" data-testid="opf-merit-stack">
      <div className="text-[10px] font-mono text-gray-500 mb-1">// merit-order dispatch stack</div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full block">
        {sorted.map((g, i) => {
          const x = pad + i * barW + 4;
          const bh = maxP > 0 ? (g.p_mw / maxP) * (h - pad - 30) : 0;
          const y = h - pad - bh;
          const colour = i / Math.max(1, sorted.length - 1) < 0.34 ? "#7CFF6B"
                        : i / Math.max(1, sorted.length - 1) < 0.67 ? "#FFCC00" : "#FF3B30";
          return (
            <g key={g.tag}>
              <rect x={x} y={y} width={barW - 8} height={bh} fill={colour} opacity="0.75" />
              <text x={x + (barW - 8) / 2} y={y - 4} textAnchor="middle"
                    fill="#FFFFFF" fontFamily="monospace" fontSize="10">{g.p_mw.toFixed(0)}</text>
              <text x={x + (barW - 8) / 2} y={h - pad + 14} textAnchor="middle"
                    fill="#8792A2" fontFamily="monospace" fontSize="9">{g.tag}</text>
              {g.at_limit !== "—" && (
                <text x={x + (barW - 8) / 2} y={h - pad + 26} textAnchor="middle"
                      fill={colour} fontFamily="monospace" fontSize="8">@ {g.at_limit}</text>
              )}
            </g>
          );
        })}
        <text x={w / 2} y={14} textAnchor="middle"
              fill="#8792A2" fontFamily="monospace" fontSize="10">
          λ = {result.lambda_$_per_MWh?.toFixed(2)} $/MWh · Σ = {result.total_dispatch_mw?.toFixed(0)} MW
        </text>
      </svg>
    </div>
  );
}

/* ── 8. Transient Stability ──────────────────────────────────── */
export function TransientStabilityCard() {
  const [mode, setMode] = useState("diagram");     // v3.2 · "table" (SMIB) | "diagram" (multi-machine)
  const [form, setForm] = useState({
    machine_mva: 100, inertia_H_s: 4, Pm_pu: 0.8,
    Pmax_prefault_pu: 1.8, Pmax_faulted_pu: 0.0, Pmax_postfault_pu: 1.5,
    fault_clearing_s: 0.15, total_time_s: 2.0, step_s: 0.005,
  });
  const [mmPayload, setMmPayload] = useState(null);   // full multi-machine payload from SLD
  const [sldEpoch, setSldEpoch] = useState(0);
  const { result, error, run, Button, payload, kind } = useStudyRunner("/studies/transient-stability", "ts-run");
  const studyName = "Transient stability";
  const patch = (p) => setForm({ ...form, ...p });
  const onSldChange = useCallback((next) => setMmPayload(next), []);
  const runIt = () => run(mode === "diagram" && mmPayload ? mmPayload : form);
  return (
    <div data-testid="study-transient-stability">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-display text-2xl font-bold">Transient stability · classical model</h3>
          <p className="text-sm text-gray-400 mt-1">
            Rotor-angle response to a 3-phase fault. <span className="font-mono">Diagram</span> mode runs a full multi-machine Y-bus + Kron reduction + RK-4 swing eqn. <span className="font-mono">Table</span> mode is the classic SMIB shortcut with an equal-area critical-clearing estimate.
          </p>
        </div>
        <ModeToggle mode={mode} setMode={setMode} prefix="ts-mode" />
      </div>
      {mode === "diagram" ? (
        <div className="mt-5">
          <MultiMachineSLD key={`ts-sld-${sldEpoch}`} onChange={onSldChange} />
          <div className="mt-2 text-[10px] font-mono text-gray-500">
            Diagram is authoritative — the graph serialises to the full multi-machine payload. Switch to <span className="text-[#00E5FF]">table</span> for the fast SMIB shortcut.
          </div>
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
          <StudyInput label="Machine MVA" type="number" value={form.machine_mva} onChange={(e) => patch({ machine_mva: Number(e.target.value) })} />
          <StudyInput label="Inertia H (s)" type="number" step="0.1" value={form.inertia_H_s} onChange={(e) => patch({ inertia_H_s: Number(e.target.value) })} data-testid="ts-H" />
          <StudyInput label="Mech P (pu)" type="number" step="0.05" value={form.Pm_pu} onChange={(e) => patch({ Pm_pu: Number(e.target.value) })} />
          <StudyInput label="Pmax pre-fault (pu)" type="number" step="0.1" value={form.Pmax_prefault_pu} onChange={(e) => patch({ Pmax_prefault_pu: Number(e.target.value) })} />
          <StudyInput label="Pmax during fault (pu)" type="number" step="0.05" value={form.Pmax_faulted_pu} onChange={(e) => patch({ Pmax_faulted_pu: Number(e.target.value) })} />
          <StudyInput label="Pmax post-fault (pu)" type="number" step="0.1" value={form.Pmax_postfault_pu} onChange={(e) => patch({ Pmax_postfault_pu: Number(e.target.value) })} />
          <StudyInput label="Fault clearing (s)" type="number" step="0.01" value={form.fault_clearing_s} onChange={(e) => patch({ fault_clearing_s: Number(e.target.value) })} data-testid="ts-tc" />
          <StudyInput label="Total time (s)" type="number" step="0.1" value={form.total_time_s} onChange={(e) => patch({ total_time_s: Number(e.target.value) })} />
          <StudyInput label="Step (s)" type="number" step="0.001" value={form.step_s} onChange={(e) => patch({ step_s: Number(e.target.value) })} />
        </div>
      )}
      <div className="mt-5 flex items-center gap-2 flex-wrap">
        <Button onClick={runIt} />
        <TemplateDropdown kind="transient_stability" onLoad={(inputs) => {
          setForm({ ...form, ...inputs });
          setSldEpoch((n) => n + 1);
        }} />
      </div>
      <StudyResultCard result={result} error={error} kind={kind} payload={payload} name={studyName} />
      {result?.mode === "multi_machine"
        ? <MultiMachineSwingCurves result={result} tc={mmPayload?.fault_clearing_s} />
        : <SwingCurve result={result} tc={form.fault_clearing_s} />}
    </div>
  );
}

/* v3.2 · Multi-line swing curve — one δ(t) per machine, colour-coded. */
function MultiMachineSwingCurves({ result, tc }) {
  const machines = result?.machines || [];
  if (!machines.length) return null;
  const w = 700, h = 240, pad = 34;
  const allPts = machines.flatMap((m) => m.trace || []);
  const times  = allPts.map((p) => p.t_s);
  const deltas = allPts.map((p) => p.delta_deg);
  if (!times.length) return null;
  const tMax = Math.max(...times, tc || 0.5);
  const dMax = Math.max(180, ...deltas, 10);
  const dMin = Math.min(-30, ...deltas, -10);
  const xFor = (t) => pad + (t / tMax) * (w - pad - 20);
  const yFor = (d) => h - pad - ((d - dMin) / (dMax - dMin)) * (h - pad - 30);
  const palette = ["#00E5FF", "#7CFF6B", "#FFCC00", "#B0A0FF", "#FF9F0A", "#FF6BB8"];
  return (
    <div className="mt-3 border border-white/10 bg-[#0A0C10] p-2" data-testid="ts-mm-swing-curves">
      <div className="text-[10px] font-mono text-gray-500 mb-1">// multi-machine δ(t) · max pair spread {result.max_pair_spread_deg?.toFixed(1)}°</div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full block">
        {[0, 90, 180].map((deg) => (
          <g key={deg}>
            <line x1={pad} x2={w - 20} y1={yFor(deg)} y2={yFor(deg)}
                  stroke={deg === 180 ? "#FF3B30" : "#ffffff10"} strokeDasharray={deg === 180 ? "3,3" : ""} />
            <text x={pad - 4} y={yFor(deg) + 3} textAnchor="end" fill={deg === 180 ? "#FF3B30" : "#8792A2"}
                  fontFamily="monospace" fontSize="9">{deg}°</text>
          </g>
        ))}
        {tc && (
          <>
            <line x1={xFor(tc)} x2={xFor(tc)} y1={pad} y2={h - pad}
                  stroke="#FFCC00" strokeDasharray="2,3" />
            <text x={xFor(tc) + 3} y={pad + 10} fill="#FFCC00"
                  fontFamily="monospace" fontSize="9">clear @ {Number(tc).toFixed(3)}s</text>
          </>
        )}
        {machines.map((m, mi) => {
          const path = (m.trace || []).map((p, i) =>
            `${i ? "L" : "M"}${xFor(p.t_s).toFixed(1)},${yFor(p.delta_deg).toFixed(1)}`).join(" ");
          return (
            <g key={m.tag}>
              <path d={path} fill="none" stroke={palette[mi % palette.length]} strokeWidth="1.6" />
              <text x={w - 22} y={26 + mi * 12} textAnchor="end"
                    fill={palette[mi % palette.length]} fontFamily="monospace" fontSize="10">
                {m.tag} · δmax {m.delta_max_deg?.toFixed(1)}°
              </text>
            </g>
          );
        })}
        <text x={w / 2} y={h - 6} textAnchor="middle"
              fill="#8792A2" fontFamily="monospace" fontSize="10">time (s)</text>
        <text x={pad} y={16} textAnchor="start"
              fill={result.stable ? "#7CFF6B" : "#FF3B30"} fontFamily="monospace" fontSize="10">
          {result.stable ? "STABLE" : "UNSTABLE"} · {machines.length} machine{machines.length > 1 ? "s" : ""} · fault @ {result.fault_bus_id}
        </text>
      </svg>
    </div>
  );
}

/* ── 9. Reliability ──────────────────────────────────────────── */
export function ReliabilityCard() {
  const [mode, setMode] = useState("diagram");     // v3.2 · "table" | "diagram"
  const [components, setComponents] = useState([
    { tag: "Utility feeder", failure_rate_per_yr: 0.02, mttr_hours: 6, configuration: "series" },
    { tag: "MV breaker",     failure_rate_per_yr: 0.005, mttr_hours: 4, configuration: "series" },
    { tag: "Transformer",    failure_rate_per_yr: 0.008, mttr_hours: 24, configuration: "series" },
  ]);
  const [customers, setCustomers] = useState(250);
  // v3.2 · bump this counter to force ReliabilitySLD to reset from
  // INITIAL_NODES. Only bumped when the user loads a template — every
  // other state change flows *out* of the SLD, so remounting would
  // clobber the user's edits.
  const [sldEpoch, setSldEpoch] = useState(0);
  const { result, error, run, Button, payload, kind } = useStudyRunner("/studies/reliability", "rel-run");
  const studyName = "Reliability";
  const patch = (i, p) => { const l = [...components]; l[i] = { ...l[i], ...p }; setComponents(l); };
  const onSldChange = useCallback(({ components: c, customers: cu }) => {
    setComponents(c); setCustomers(cu);
  }, []);
  return (
    <div data-testid="study-reliability">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-display text-2xl font-bold">Reliability · IEEE 1366 / Gold Book</h3>
          <p className="text-sm text-gray-400 mt-1">
            Component reliability block diagram. Tag each component as <span className="font-mono">series</span> (adds failure rate) or <span className="font-mono">parallel</span> (redundant path). Returns MTBF, availability %, SAIFI, SAIDI.
          </p>
        </div>
        <ModeToggle mode={mode} setMode={setMode} prefix="rel-mode" />
      </div>
      {mode === "diagram" ? (
        <div className="mt-5">
          <ReliabilitySLD key={`rel-sld-${sldEpoch}`} onChange={onSldChange} />
          <div className="mt-2 text-[10px] font-mono text-gray-500">
            Diagram is authoritative in this mode — switch to <span className="text-[#00E5FF]">table</span> to hand-edit component rows.
          </div>
        </div>
      ) : (
        <>
          <div className="mt-4">
            <StudyInput label="Customers on feeder" type="number" value={customers} onChange={(e) => setCustomers(Number(e.target.value))} data-testid="rel-customers" />
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between mb-1">
              <StudyLabel text="Components" />
              <button onClick={() => setComponents([...components, { tag: `C${components.length+1}`, failure_rate_per_yr: 0.01, mttr_hours: 4, configuration: "series" }])} className="text-xs text-[#00E5FF] hover:underline"><Plus size={12} className="inline" /> add</button>
            </div>
            {components.map((c, i) => (
              <div key={i} className="grid grid-cols-12 gap-1 mb-1" data-testid={`rel-row-${i}`}>
                <input value={c.tag} onChange={(e) => patch(i, { tag: e.target.value })} className="col-span-4 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono" placeholder="tag" />
                <input type="number" step="0.001" value={c.failure_rate_per_yr} onChange={(e) => patch(i, { failure_rate_per_yr: Number(e.target.value) })} className="col-span-3 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono" placeholder="λ/yr" />
                <input type="number" step="0.5" value={c.mttr_hours} onChange={(e) => patch(i, { mttr_hours: Number(e.target.value) })} className="col-span-2 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono" placeholder="MTTR hr" />
                <select value={c.configuration} onChange={(e) => patch(i, { configuration: e.target.value })} className="col-span-2 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono">
                  <option value="series">series</option><option value="parallel">parallel</option>
                </select>
                <button onClick={() => setComponents(components.filter((_, x) => x !== i))} className="col-span-1 text-[#FF3B30] text-xs"><Trash size={12} /></button>
              </div>
            ))}
          </div>
        </>
      )}
      <div className="mt-5 flex items-center gap-2 flex-wrap">
        <Button onClick={() => run({ components, customers })} />
        <TemplateDropdown kind="reliability" onLoad={(inputs) => {
          setComponents(inputs.components || []);
          setCustomers(inputs.customers || 100);
          setSldEpoch((n) => n + 1);      // reset diagram to match new template
        }} />
      </div>
      <StudyResultCard result={result} error={error} kind={kind} payload={payload} name={studyName} />
      <MonteCarloSection components={components} customers={customers} />
      <MonteCarloCompareSection baselineComponents={components} customers={customers} />
    </div>
  );
}

/* v3.2 · Monte-Carlo tail-metrics simulator on top of the same RBD.
   Uses `/api/studies/reliability/montecarlo`. Renders p50/p90/p99 summary
   and a Recharts histogram of annual downtime hours across N samples. */
function MonteCarloSection({ components, customers }) {
  const [nSamples,  setN]     = useState(10000);
  const [horizon,   setH]     = useState(1.0);
  const [costPerHr, setCost]  = useState(5000);
  const [mc, setMc]           = useState(null);
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState("");

  const run = async () => {
    setBusy(true); setError(""); setMc(null);
    try {
      const { data } = await api.post("/studies/reliability/montecarlo", {
        components, customers,
        horizon_years: horizon,
        n_samples: nSamples,
        cost_per_hour_outage_usd: costPerHr,
      });
      setMc(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Monte-Carlo failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 border border-white/10 bg-[#0A0C10] p-4" data-testid="rel-mc-section">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="label-tag">// monte-carlo tail metrics</div>
          <h4 className="font-display text-lg font-bold mt-0.5">Simulate feeder downtime</h4>
          <p className="text-[11px] text-gray-500 mt-1 max-w-2xl">
            Runs N samples over the horizon using the components above. Returns p50 / p90 / p99 annual downtime + expected outage cost. p90 and p99 are what utility clients use for SLA reserving.
          </p>
        </div>
        <button onClick={run} disabled={busy || !components.length}
          className={`px-3 py-1.5 text-xs font-mono border ${busy ? "border-white/10 text-gray-500" : "border-[#00E5FF] text-[#00E5FF] hover:bg-[#00E5FF]/10"}`}
          data-testid="rel-mc-run">
          {busy ? "running…" : "▶ run monte-carlo"}
        </button>
      </div>
      <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2">
        <StudyInput label="Samples (N)" type="number" step="1000" value={nSamples} onChange={(e) => setN(Number(e.target.value))} data-testid="rel-mc-n" />
        <StudyInput label="Horizon (years)" type="number" step="0.5" value={horizon} onChange={(e) => setH(Number(e.target.value))} data-testid="rel-mc-horizon" />
        <StudyInput label="Cost / hour ($)" type="number" step="500" value={costPerHr} onChange={(e) => setCost(Number(e.target.value))} data-testid="rel-mc-cost" />
      </div>
      {error && <div className="mt-3 text-xs font-mono text-[#FF3B30]" data-testid="rel-mc-error">{error}</div>}
      {mc && <MonteCarloResult mc={mc} />}
    </div>
  );
}

function MonteCarloResult({ mc }) {
  const cells = [
    { k: "mean",        v: `${mc.mean_downtime_h?.toFixed(2)} h`, sub: "±σ " + mc.std_downtime_h?.toFixed(1) },
    { k: "p50",         v: `${mc.p50_downtime_h?.toFixed(2)} h`,  sub: `avail ${mc.availability_mean_pct?.toFixed(3)} %` },
    { k: "p90",         v: `${mc.p90_downtime_h?.toFixed(2)} h`,  sub: `$${(mc.p90_annual_cost_usd/1000).toFixed(1)}k` },
    { k: "p99",         v: `${mc.p99_downtime_h?.toFixed(2)} h`,  sub: `$${(mc.p99_annual_cost_usd/1000).toFixed(1)}k` },
    { k: "cost",        v: `$${(mc.expected_annual_cost_usd/1000).toFixed(1)}k`, sub: `${mc.n_samples} samples`, label: "E[cost/yr]" },
  ];
  const bins = mc.histogram || [];
  const maxCount = Math.max(1, ...bins.map((b) => b.count));
  const w = 700, h = 180, pad = 30;
  const bw = (w - pad - 20) / Math.max(1, bins.length);
  return (
    <div className="mt-4 space-y-3" data-testid="rel-mc-result">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {cells.map((c) => (
          <div key={c.k} className="border border-white/10 bg-[#12151C] p-2" data-testid={`rel-mc-cell-${c.k}`}>
            <div className="text-[9px] font-mono text-gray-500 uppercase">{c.label || c.k}</div>
            <div className="text-lg font-display font-bold text-[#00E5FF] leading-tight">{c.v}</div>
            <div className="text-[10px] font-mono text-gray-500">{c.sub}</div>
          </div>
        ))}
      </div>
      <div className="border border-white/10 bg-[#0A0C10] p-2">
        <div className="text-[10px] font-mono text-gray-500 mb-1">// annual downtime histogram (hours)</div>
        <svg viewBox={`0 0 ${w} ${h}`} className="w-full block">
          {bins.map((b, i) => {
            const bh = (b.count / maxCount) * (h - pad - 20);
            const x = pad + i * bw + 2;
            const y = h - pad - bh;
            const isP90 = mc.p90_downtime_h >= b.bin_lo_h && mc.p90_downtime_h < b.bin_hi_h;
            const isP99 = mc.p99_downtime_h >= b.bin_lo_h && mc.p99_downtime_h < b.bin_hi_h;
            const fill = isP99 ? "#FF3B30" : isP90 ? "#FFCC00" : "#00E5FF";
            return (
              <g key={i}>
                <rect x={x} y={y} width={bw - 3} height={bh} fill={fill} opacity="0.75" />
                {(i === 0 || i === bins.length - 1) && (
                  <text x={x} y={h - pad + 12} fill="#8792A2" fontFamily="monospace" fontSize="9">
                    {b.bin_lo_h.toFixed(0)}h
                  </text>
                )}
              </g>
            );
          })}
          <text x={w / 2} y={14} textAnchor="middle" fill="#8792A2" fontFamily="monospace" fontSize="10">
            {mc.n_samples} samples · horizon {mc.horizon_years} yr · <tspan fill="#FFCC00">p90</tspan> / <tspan fill="#FF3B30">p99</tspan> highlighted
          </text>
        </svg>
      </div>
    </div>
  );
}

/* v3.2.2 · Compare two designs · side-by-side Monte-Carlo overlay.

   Utility clients pick between redundancy schemes (Design A vs B) —
   this section lets them mutate the baseline RBD into a "Design B",
   re-run MC on both, and see the expected-cost delta with a one-line
   recommendation. Overlayed histograms highlight where Design B's
   tail sits relative to A. */
function MonteCarloCompareSection({ baselineComponents, customers }) {
  const [designB, setDesignB] = useState(null);      // starts null → "clone baseline"
  const [nSamples,  setN]     = useState(10000);
  const [horizon,   setH]     = useState(1.0);
  const [costPerHr, setCost]  = useState(5000);
  const [mcA, setMcA] = useState(null);
  const [mcB, setMcB] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Sync designB with baseline whenever the baseline changes and we haven't customized it.
  const dB = designB ?? baselineComponents;
  const patch = (i, p) => {
    const l = [...(designB ?? baselineComponents)];
    l[i] = { ...l[i], ...p };
    setDesignB(l);
  };
  const addB = () => setDesignB([...(designB ?? baselineComponents),
    { tag: `B${(designB ?? baselineComponents).length + 1}`, failure_rate_per_yr: 0.01, mttr_hours: 4, configuration: "parallel" }]);
  const resetB = () => setDesignB(null);

  const run = async () => {
    setBusy(true); setError(""); setMcA(null); setMcB(null);
    try {
      const body = { customers, horizon_years: horizon, n_samples: nSamples,
                     cost_per_hour_outage_usd: costPerHr };
      const [{ data: a }, { data: b }] = await Promise.all([
        api.post("/studies/reliability/montecarlo", { ...body, components: baselineComponents }),
        api.post("/studies/reliability/montecarlo", { ...body, components: dB }),
      ]);
      setMcA(a); setMcB(b);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Compare failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-4 border border-white/10 bg-[#0A0C10] p-4" data-testid="rel-mc-compare-section">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="label-tag">// design comparison</div>
          <h4 className="font-display text-lg font-bold mt-0.5">Compare two designs</h4>
          <p className="text-[11px] text-gray-500 mt-1 max-w-2xl">
            Design A = your baseline above. Edit or add components below to define Design B (parallel branch, faster MTTR, redundant transformer…) and run MC on both simultaneously to see the tail-cost delta.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={resetB} className="text-[10px] text-gray-400 hover:text-white underline"
                  data-testid="rel-mc-compare-reset-b">reset B to A</button>
          <button onClick={addB} className="text-[10px] text-[#00E5FF] hover:underline"
                  data-testid="rel-mc-compare-add-b">+ redundant branch</button>
          <button onClick={run} disabled={busy || !baselineComponents.length}
            className={`px-3 py-1.5 text-xs font-mono border ${busy ? "border-white/10 text-gray-500" : "border-[#00E5FF] text-[#00E5FF] hover:bg-[#00E5FF]/10"}`}
            data-testid="rel-mc-compare-run">
            {busy ? "running…" : "▶ compare A vs B"}
          </button>
        </div>
      </div>
      <div className="mt-3">
        <div className="text-[10px] font-mono text-gray-500 mb-1">// design B components (starts as clone of A)</div>
        {dB.map((c, i) => (
          <div key={i} className="grid grid-cols-12 gap-1 mb-1" data-testid={`rel-mc-b-row-${i}`}>
            <input value={c.tag} onChange={(e) => patch(i, { tag: e.target.value })} className="col-span-4 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono" placeholder="tag" />
            <input type="number" step="0.001" value={c.failure_rate_per_yr} onChange={(e) => patch(i, { failure_rate_per_yr: Number(e.target.value) })} className="col-span-3 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono" placeholder="λ/yr" />
            <input type="number" step="0.5" value={c.mttr_hours} onChange={(e) => patch(i, { mttr_hours: Number(e.target.value) })} className="col-span-2 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono" placeholder="MTTR hr" />
            <select value={c.configuration} onChange={(e) => patch(i, { configuration: e.target.value })} className="col-span-3 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono">
              <option value="series">series</option><option value="parallel">parallel</option>
            </select>
          </div>
        ))}
      </div>
      <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2">
        <StudyInput label="Samples (N)" type="number" step="1000" value={nSamples} onChange={(e) => setN(Number(e.target.value))} />
        <StudyInput label="Horizon (years)" type="number" step="0.5" value={horizon} onChange={(e) => setH(Number(e.target.value))} />
        <StudyInput label="Cost / hour ($)" type="number" step="500" value={costPerHr} onChange={(e) => setCost(Number(e.target.value))} />
      </div>
      {error && <div className="mt-3 text-xs font-mono text-[#FF3B30]" data-testid="rel-mc-compare-error">{error}</div>}
      {mcA && mcB && <MonteCarloCompareResult mcA={mcA} mcB={mcB} />}
    </div>
  );
}

function MonteCarloCompareResult({ mcA, mcB }) {
  const dExp = mcB.expected_annual_cost_usd - mcA.expected_annual_cost_usd;
  const dP99 = mcB.p99_annual_cost_usd     - mcA.p99_annual_cost_usd;
  const winner = dExp < 0 ? "B" : dExp > 0 ? "A" : "tie";
  const winnerCopy = winner === "tie"
    ? "Roughly identical — the two designs have equivalent expected cost."
    : `Design ${winner} wins by $${(Math.abs(dExp)/1000).toFixed(1)}k / year on expected outage cost` +
      ((winner === "B" && dP99 < 0) ? ` and $${(Math.abs(dP99)/1000).toFixed(1)}k on the p99 tail.` : ".");
  const rows = [
    ["mean",    "mean_downtime_h",          " h"],
    ["p90",     "p90_downtime_h",           " h"],
    ["p99",     "p99_downtime_h",           " h"],
    ["E[cost]", "expected_annual_cost_usd", ""],
    ["p99 $",   "p99_annual_cost_usd",      ""],
  ];
  // Overlay histograms: A cyan, B amber, with shared x-scale.
  const binsA = mcA.histogram || [];
  const binsB = mcB.histogram || [];
  const lo = Math.min(binsA[0]?.bin_lo_h ?? 0, binsB[0]?.bin_lo_h ?? 0);
  const hi = Math.max(binsA[binsA.length - 1]?.bin_hi_h ?? 1,
                      binsB[binsB.length - 1]?.bin_hi_h ?? 1);
  const w = 700, h = 200, pad = 30;
  const xFor = (v) => pad + ((v - lo) / Math.max(1e-6, hi - lo)) * (w - pad - 20);
  const maxCount = Math.max(1, ...binsA.map((b) => b.count), ...binsB.map((b) => b.count));
  return (
    <div className="mt-4 space-y-3" data-testid="rel-mc-compare-result">
      <div className={`p-3 border ${winner === "B" ? "border-[#7CFF6B]/60 bg-[#7CFF6B]/5 text-[#7CFF6B]"
                                                    : winner === "A" ? "border-[#00E5FF]/60 bg-[#00E5FF]/5 text-[#00E5FF]"
                                                                     : "border-white/10 text-gray-300"}
                        text-xs font-mono`}
           data-testid="rel-mc-compare-recommendation">
        <span className="font-bold">recommendation ·</span> {winnerCopy}
      </div>
      <table className="w-full text-[11px] font-mono border border-white/10 bg-[#12151C]">
        <thead className="text-gray-500 bg-black/30">
          <tr>
            <th className="text-left px-2 py-1">metric</th>
            <th className="text-right px-2 py-1 text-[#00E5FF]">Design A</th>
            <th className="text-right px-2 py-1 text-[#FFCC00]">Design B</th>
            <th className="text-right px-2 py-1">Δ (B − A)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([label, key, suffix]) => {
            const a = mcA[key]; const b = mcB[key]; const d = b - a;
            const isCost = key.includes("cost");
            const fmt = (v) => isCost ? `$${(v/1000).toFixed(1)}k` : `${v.toFixed(2)}${suffix}`;
            return (
              <tr key={key} className="border-t border-white/5" data-testid={`rel-mc-compare-row-${label.replace(/\W+/g, "-")}`}>
                <td className="px-2 py-1 text-gray-400">{label}</td>
                <td className="px-2 py-1 text-right text-[#00E5FF]">{fmt(a)}</td>
                <td className="px-2 py-1 text-right text-[#FFCC00]">{fmt(b)}</td>
                <td className={`px-2 py-1 text-right ${d < 0 ? "text-[#7CFF6B]" : d > 0 ? "text-[#FF3B30]" : "text-gray-400"}`}>{fmt(d)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="border border-white/10 bg-[#0A0C10] p-2">
        <div className="text-[10px] font-mono text-gray-500 mb-1">// overlayed histogram · <span className="text-[#00E5FF]">A</span> vs <span className="text-[#FFCC00]">B</span></div>
        <svg viewBox={`0 0 ${w} ${h}`} className="w-full block">
          {binsA.map((b, i) => {
            const bh = (b.count / maxCount) * (h - pad - 20);
            const x1 = xFor(b.bin_lo_h), x2 = xFor(b.bin_hi_h);
            return (
              <rect key={`a-${i}`} x={x1 + 1} y={h - pad - bh} width={Math.max(2, x2 - x1 - 2)} height={bh}
                    fill="#00E5FF" opacity="0.55" />
            );
          })}
          {binsB.map((b, i) => {
            const bh = (b.count / maxCount) * (h - pad - 20);
            const x1 = xFor(b.bin_lo_h), x2 = xFor(b.bin_hi_h);
            return (
              <rect key={`b-${i}`} x={x1 + 1} y={h - pad - bh} width={Math.max(2, x2 - x1 - 2)} height={bh}
                    fill="#FFCC00" opacity="0.55" />
            );
          })}
          <line x1={xFor(mcA.p99_downtime_h)} x2={xFor(mcA.p99_downtime_h)} y1={pad} y2={h - pad}
                stroke="#00E5FF" strokeDasharray="2,3" />
          <line x1={xFor(mcB.p99_downtime_h)} x2={xFor(mcB.p99_downtime_h)} y1={pad} y2={h - pad}
                stroke="#FFCC00" strokeDasharray="2,3" />
          <text x={w / 2} y={h - 8} textAnchor="middle" fill="#8792A2" fontFamily="monospace" fontSize="10">
            annual downtime (h) — dashed lines mark each design&apos;s p99
          </text>
        </svg>
      </div>
    </div>
  );
}

/* ── 10. Cable Ampacity ──────────────────────────────────────── */
export function CableAmpacityCard() {
  const [mode, setMode] = useState("diagram");     // v3.2 · "table" | "diagram"
  const [form, setForm] = useState({
    awg_or_kcmil: "4/0", ambient_c: 30, conductors_in_raceway: 3,
    installation: "raceway", depth_m: 0.6, length_m: 30, load_amps: 200,
  });
  // v3.2 · Only bump on template load — see ReliabilityCard for the
  // full explanation. Diagram is authoritative in `diagram` mode; state
  // flows one-way out of the SLD, so a data-driven `key` would remount
  // the canvas on every keystroke and wipe user edits.
  const [sldEpoch, setSldEpoch] = useState(0);
  const { result, error, run, Button, payload, kind } = useStudyRunner("/studies/cable-ampacity", "amp-run");
  const studyName = "Cable ampacity";
  const patch = (p) => setForm((f) => ({ ...f, ...p }));
  const onSldChange = useCallback((next) => setForm(next), []);
  return (
    <div data-testid="study-cable-ampacity">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-display text-2xl font-bold">Cable ampacity · NEC 310.16</h3>
          <p className="text-sm text-gray-400 mt-1">
            Copper conductor ampacity (75 °C) with ambient + grouping + burial-depth derating. Verdict flags OK / MARGINAL / OVERLOAD against your load.
          </p>
        </div>
        <ModeToggle mode={mode} setMode={setMode} prefix="amp-mode" />
      </div>
      {mode === "diagram" ? (
        <div className="mt-5">
          <CableAmpacitySLD key={`amp-sld-${sldEpoch}`} onChange={onSldChange} />
          <div className="mt-2 text-[10px] font-mono text-gray-500">
            Diagram is authoritative in this mode — add/remove conductor nodes to change the grouping derate; switch to <span className="text-[#00E5FF]">table</span> for row-based editing.
          </div>
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
          <StudySelect label="Size (AWG / kcmil)" value={form.awg_or_kcmil} onChange={(e) => patch({ awg_or_kcmil: e.target.value })}
            options={["14","12","10","8","6","4","3","2","1","1/0","2/0","3/0","4/0","250","300","350","400","500","600","700","750","800","1000"]}
            data-testid="amp-size" />
          <StudyInput label="Ambient temp (°C)" type="number" value={form.ambient_c} onChange={(e) => patch({ ambient_c: Number(e.target.value) })} data-testid="amp-amb" />
          <StudyInput label="Conductors in raceway" type="number" value={form.conductors_in_raceway} onChange={(e) => patch({ conductors_in_raceway: Number(e.target.value) })} data-testid="amp-count-table" />
          <StudySelect label="Installation" value={form.installation} onChange={(e) => patch({ installation: e.target.value })}
            options={["raceway","buried","tray"]} data-testid="amp-inst" />
          <StudyInput label="Depth (m, buried)" type="number" step="0.1" value={form.depth_m} onChange={(e) => patch({ depth_m: Number(e.target.value) })} />
          <StudyInput label="Length (m)" type="number" value={form.length_m} onChange={(e) => patch({ length_m: Number(e.target.value) })} />
          <StudyInput label="Load (A)" type="number" value={form.load_amps} onChange={(e) => patch({ load_amps: Number(e.target.value) })} data-testid="amp-load" />
        </div>
      )}
      <div className="mt-5 flex items-center gap-2 flex-wrap">
        <Button onClick={() => run(form)} />
        <TemplateDropdown kind="cable_ampacity" onLoad={(inputs) => {
          setForm((f) => ({ ...f, ...inputs }));
          setSldEpoch((n) => n + 1);      // reset diagram to match new template
        }} />
      </div>
      <StudyResultCard result={result} error={error} kind={kind} payload={payload} name={studyName} />
    </div>
  );
}

/* ── 11. Optimal Power Flow ──────────────────────────────────── */
export function OpfCard() {
  const [mode, setMode] = useState("lossless");     // v3.2 · "lossless" | "with_losses"
  const [gens, setGens] = useState([
    { tag: "G1", bus_id: "B1", p_min_mw: 50, p_max_mw: 300, cost_a: 0, cost_b: 18, cost_c: 0.008 },
    { tag: "G2", bus_id: "B3", p_min_mw: 30, p_max_mw: 200, cost_a: 0, cost_b: 22, cost_c: 0.015 },
  ]);
  const [buses, setBuses] = useState([
    { id: "B1", load_mw: 0,  slack: true  },
    { id: "B2", load_mw: 120, slack: false },
    { id: "B3", load_mw: 80,  slack: false },
  ]);
  const [lines, setLines] = useState([
    { from_bus: "B1", to_bus: "B2", R_pu: 0.02, X_pu: 0.10, limit_mw: 150 },
    { from_bus: "B2", to_bus: "B3", R_pu: 0.02, X_pu: 0.10, limit_mw: 150 },
    { from_bus: "B1", to_bus: "B3", R_pu: 0.03, X_pu: 0.15, limit_mw: 150 },
  ]);
  const [load, setLoad] = useState(350);
  const { result, error, run, Button, payload, kind } = useStudyRunner("/studies/opf", "opf-run");
  const studyName = "Optimal power flow";
  const patchGen  = (i, p) => { const l = [...gens]; l[i] = { ...l[i], ...p }; setGens(l); };
  const patchBus  = (i, p) => { const l = [...buses]; l[i] = { ...l[i], ...p }; setBuses(l); };
  const patchLine = (i, p) => { const l = [...lines]; l[i] = { ...l[i], ...p }; setLines(l); };

  const runIt = () => {
    if (mode === "lossless") {
      run({ generators: gens, total_load_mw: load, mode: "lossless" });
    } else {
      // Both "with_losses" and "ac" use the network payload.
      run({ generators: gens, buses, lines, mode });
    }
  };

  return (
    <div data-testid="study-opf">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-display text-2xl font-bold">Optimal power flow · dispatch</h3>
          <p className="text-sm text-gray-400 mt-1">
            Least-cost economic dispatch with quadratic cost curves (C = a + bP + cP²). <span className="font-mono">lossless</span> mode solves λ-iteration ED against a lumped load. <span className="font-mono">with losses</span> adds a DC power-flow and updates dispatch to cover line I²R losses, plus per-bus LMPs.
          </p>
        </div>
        <ModeToggle mode={mode} setMode={setMode} prefix="opf-mode"
          options={[
            { value: "lossless",    label: "lossless" },
            { value: "with_losses", label: "DC + losses" },
            { value: "ac",          label: "AC / N-R" },
          ]} />
      </div>
      {mode === "lossless" && (
        <div className="mt-4">
          <StudyInput label="Total system load (MW)" type="number" value={load} onChange={(e) => setLoad(Number(e.target.value))} data-testid="opf-load" />
        </div>
      )}
      {mode !== "lossless" && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <div className="flex items-center justify-between mb-1">
              <StudyLabel text="Buses (loads + slack)" />
              <button onClick={() => setBuses([...buses, { id: `B${buses.length+1}`, load_mw: 0, slack: false }])} className="text-xs text-[#00E5FF] hover:underline" data-testid="opf-add-bus"><Plus size={12} className="inline" /> add bus</button>
            </div>
            {buses.map((b, i) => (
              <div key={i} className="grid grid-cols-12 gap-1 mb-1" data-testid={`opf-bus-${i}`}>
                <input value={b.id} onChange={(e) => patchBus(i, { id: e.target.value })} className="col-span-3 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono" placeholder="id" />
                <input type="number" value={b.load_mw} onChange={(e) => patchBus(i, { load_mw: Number(e.target.value) })} className="col-span-4 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono" placeholder="load MW" />
                <label className="col-span-4 flex items-center gap-1 text-xs font-mono text-gray-400">
                  <input type="checkbox" checked={b.slack} onChange={(e) => patchBus(i, { slack: e.target.checked })} /> slack
                </label>
                <button onClick={() => setBuses(buses.filter((_, x) => x !== i))} className="col-span-1 text-[#FF3B30] text-xs"><Trash size={12} /></button>
              </div>
            ))}
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <StudyLabel text="Lines (R/X pu, limit MW)" />
              <button onClick={() => setLines([...lines, { from_bus: buses[0]?.id || "B1", to_bus: buses[1]?.id || "B2", R_pu: 0.02, X_pu: 0.1, limit_mw: 100 }])} className="text-xs text-[#00E5FF] hover:underline" data-testid="opf-add-line"><Plus size={12} className="inline" /> add line</button>
            </div>
            {lines.map((ln, i) => (
              <div key={i} className="grid grid-cols-12 gap-1 mb-1" data-testid={`opf-line-${i}`}>
                <input value={ln.from_bus} onChange={(e) => patchLine(i, { from_bus: e.target.value })} className="col-span-3 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono" placeholder="from" />
                <input value={ln.to_bus} onChange={(e) => patchLine(i, { to_bus: e.target.value })} className="col-span-3 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono" placeholder="to" />
                <input type="number" step="0.01" value={ln.R_pu} onChange={(e) => patchLine(i, { R_pu: Number(e.target.value) })} className="col-span-2 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono" placeholder="R" />
                <input type="number" step="0.01" value={ln.X_pu} onChange={(e) => patchLine(i, { X_pu: Number(e.target.value) })} className="col-span-2 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono" placeholder="X" />
                <input type="number" value={ln.limit_mw} onChange={(e) => patchLine(i, { limit_mw: Number(e.target.value) })} className="col-span-1 bg-black/40 border border-white/10 px-1 py-1 text-xs font-mono" placeholder="MW" />
                <button onClick={() => setLines(lines.filter((_, x) => x !== i))} className="col-span-1 text-[#FF3B30] text-xs"><Trash size={12} /></button>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="mt-4">
        <div className="flex items-center justify-between mb-1">
          <StudyLabel text="Generators (cost curve C = a + b·P + c·P²)" />
          <button onClick={() => setGens([...gens, { tag: `G${gens.length+1}`, bus_id: buses[0]?.id || "B1", p_min_mw: 20, p_max_mw: 150, cost_a: 0, cost_b: 25, cost_c: 0.02 }])} className="text-xs text-[#00E5FF] hover:underline"><Plus size={12} className="inline" /> add gen</button>
        </div>
        {gens.map((g, i) => (
          <div key={i} className="grid grid-cols-12 gap-1 mb-1" data-testid={`opf-gen-${i}`}>
            <input value={g.tag} onChange={(e) => patchGen(i, { tag: e.target.value })} className="col-span-2 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono" placeholder="tag" />
            {mode === "with_losses" && (
              <input value={g.bus_id || ""} onChange={(e) => patchGen(i, { bus_id: e.target.value })} className="col-span-2 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono" placeholder="bus" />
            )}
            <input type="number" value={g.p_min_mw} onChange={(e) => patchGen(i, { p_min_mw: Number(e.target.value) })} className={`${mode === "with_losses" ? "col-span-1" : "col-span-2"} bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono`} placeholder="P min" />
            <input type="number" value={g.p_max_mw} onChange={(e) => patchGen(i, { p_max_mw: Number(e.target.value) })} className={`${mode === "with_losses" ? "col-span-2" : "col-span-2"} bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono`} placeholder="P max" />
            <input type="number" step="0.5" value={g.cost_b} onChange={(e) => patchGen(i, { cost_b: Number(e.target.value) })} className="col-span-2 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono" placeholder="b" />
            <input type="number" step="0.001" value={g.cost_c} onChange={(e) => patchGen(i, { cost_c: Number(e.target.value) })} className={`${mode === "with_losses" ? "col-span-2" : "col-span-3"} bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono`} placeholder="c" />
            <button onClick={() => setGens(gens.filter((_, x) => x !== i))} className="col-span-1 text-[#FF3B30] text-xs"><Trash size={12} /></button>
          </div>
        ))}
      </div>
      <div className="mt-5 flex items-center gap-2 flex-wrap">
        <Button onClick={runIt} />
        <TemplateDropdown kind="opf" onLoad={(inputs) => {
          setGens(inputs.generators || []);
          setLoad(inputs.total_load_mw || 100);
        }} />
      </div>
      <StudyResultCard result={result} error={error} kind={kind} payload={payload} name={studyName} />
      <MeritStack result={result} />
      {(result?.mode === "with_losses" || result?.mode === "ac") && <OpfLossesPanel result={result} />}
      {result?.mode === "ac" && <OpfAcPanel result={result} />}
    </div>
  );
}

/* v3.2.2 · Extra panel for AC-OPF — per-bus |V|, angle, Q status. */
function OpfAcPanel({ result }) {
  const rows = result.buses || [];
  return (
    <div className="mt-3 border border-white/10 bg-[#0A0C10] p-2" data-testid="opf-ac-panel">
      <div className="text-[10px] font-mono text-gray-500 mb-1">
        // AC N-R state · slack {result.slack_bus} · converged={String(result.converged)} in {result.iterations} outer iters · V-viol={String(result.any_v_violation)} · Q-viol={String(result.any_q_violation)}
      </div>
      <table className="w-full text-[11px] font-mono">
        <thead className="text-gray-500">
          <tr>
            <th className="text-left">bus</th>
            <th className="text-right">|V| pu</th>
            <th className="text-right">θ°</th>
            <th className="text-right">P inj MW</th>
            <th className="text-right">Q inj Mvar</th>
            <th className="text-right">V status</th>
            <th className="text-right">Q status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className={r.v_status !== "OK" || r.q_status === "OUT" ? "text-[#FF3B30]" : "text-white"}>
              <td>{r.bus_id}</td>
              <td className="text-right">{r.V_pu.toFixed(4)}</td>
              <td className="text-right">{r.angle_deg.toFixed(2)}</td>
              <td className="text-right">{r.P_inj_mw.toFixed(2)}</td>
              <td className="text-right">{r.Q_inj_mvar.toFixed(2)}</td>
              <td className="text-right">{r.v_status}</td>
              <td className="text-right">{r.q_status}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {result.auto_corrected && <OpfCorrectionPanel result={result} />}
    </div>
  );
}

/* v3.2.3 · Auto-correction diagnostic — shows what the solver did
   (V-setpoint nudges + Q-stress cost surcharges) and whether any
   violations remain after the single corrective sweep. */
function OpfCorrectionPanel({ result }) {
  const adj  = result.v_setpoint_adjustments || [];
  const surc = result.q_stress_penalty_applied || [];
  const preV = (result.pre_correction_v_violations || []).length;
  const preQ = (result.pre_correction_q_violations || []).length;
  const resV = result.residual_v_violations ?? 0;
  const resQ = result.residual_q_violations ?? 0;
  const cleared = (preV + preQ) - (resV + resQ);
  // If violations were found but nothing moved, it means the violating
  // bus is a pure load bus with no PV controllable device attached.
  // Surface a friendly hint so the user knows to add a gen / shunt.
  const noControlHint = (preV + preQ) > 0 && adj.length === 0 && surc.length === 0;
  return (
    <div className="mt-3 border-t border-white/10 pt-3" data-testid="opf-correction-panel">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-2">
        <div>
          <div className="label-tag">// v/q auto-correction</div>
          <div className="text-xs font-mono text-gray-400">
            {preV} V + {preQ} Q pre-viol{" "}
            <span className="text-[#00E5FF]">→</span>{" "}
            {resV} V + {resQ} Q residual{" "}
            <span className={cleared > 0 ? "text-[#7CFF6B]" : cleared === 0 ? "text-[#FFCC00]" : "text-[#FF3B30]"}>
              ({cleared >= 0 ? `−${cleared} cleared` : `+${Math.abs(cleared)} new`})
            </span>
          </div>
        </div>
      </div>
      {noControlHint && (
        <div className="mb-2 p-2 border border-[#FFCC00]/40 bg-[#FFCC00]/5 text-[10px] font-mono text-[#FFCC00]"
             data-testid="opf-correction-no-control-hint">
          no controllable device on the violating bus — add a generator or a switched shunt to enable V-setpoint control here.
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div className="border border-white/10 bg-[#12151C] p-2">
          <div className="text-[10px] font-mono text-gray-500 mb-1">// V-setpoint nudges</div>
          {adj.length === 0
            ? <div className="text-[11px] font-mono text-gray-500">— none applied</div>
            : (
              <table className="w-full text-[11px] font-mono">
                <thead className="text-gray-500">
                  <tr><th className="text-left">bus</th><th className="text-right">before</th><th className="text-right">after</th><th className="text-right">Δ</th></tr>
                </thead>
                <tbody>
                  {adj.map((a, i) => (
                    <tr key={i} className="text-[#00E5FF]" data-testid={`opf-vadj-${a.bus_id}`}>
                      <td>{a.bus_id}</td>
                      <td className="text-right">{a.before.toFixed(4)}</td>
                      <td className="text-right">{a.after.toFixed(4)}</td>
                      <td className={`text-right ${a.after > a.before ? "text-[#7CFF6B]" : "text-[#FFCC00]"}`}>
                        {a.after > a.before ? "+" : ""}{(a.after - a.before).toFixed(4)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </div>
        <div className="border border-white/10 bg-[#12151C] p-2">
          <div className="text-[10px] font-mono text-gray-500 mb-1">// Q-stress cost surcharges</div>
          {surc.length === 0
            ? <div className="text-[11px] font-mono text-gray-500">— none applied</div>
            : (
              <table className="w-full text-[11px] font-mono">
                <thead className="text-gray-500">
                  <tr><th className="text-left">gen</th><th className="text-right">Q now</th><th className="text-right">$/MWh added</th></tr>
                </thead>
                <tbody>
                  {surc.map((s, i) => (
                    <tr key={i} className="text-[#FFCC00]" data-testid={`opf-qsurcharge-${s.tag}`}>
                      <td>{s.tag} @ {s.bus_id}</td>
                      <td className="text-right">{s.q_now_mvar.toFixed(1)}</td>
                      <td className="text-right">+{s.surcharge_$_per_MWh.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </div>
      </div>
    </div>
  );
}

/* v3.2 · Extra panel for AC-OPF (with losses) — per-line loading & loss, per-bus LMP. */
function OpfLossesPanel({ result }) {
  const lineRows = result.lines || [];
  const lmps     = result.lmps  || [];
  return (
    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="opf-losses-panel">
      <div className="border border-white/10 bg-[#0A0C10] p-2">
        <div className="text-[10px] font-mono text-gray-500 mb-1">
          // line flows · Σ losses {result.total_losses_mw?.toFixed(2)} MW ({result.loss_pct?.toFixed(2)} % of load)
        </div>
        <table className="w-full text-[11px] font-mono">
          <thead className="text-gray-500">
            <tr><th className="text-left">from → to</th><th className="text-right">flow MW</th><th className="text-right">loss MW</th><th className="text-right">load %</th></tr>
          </thead>
          <tbody>
            {lineRows.map((ln, i) => (
              <tr key={i} className={ln.overloaded ? "text-[#FF3B30]" : "text-white"}>
                <td>{ln.from_bus} → {ln.to_bus}</td>
                <td className="text-right">{ln.flow_mw.toFixed(2)}</td>
                <td className="text-right">{ln.loss_mw.toFixed(3)}</td>
                <td className="text-right">{ln.loading_pct.toFixed(1)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="border border-white/10 bg-[#0A0C10] p-2">
        <div className="text-[10px] font-mono text-gray-500 mb-1">// LMPs ($/MWh)</div>
        <table className="w-full text-[11px] font-mono">
          <thead className="text-gray-500"><tr><th className="text-left">bus</th><th className="text-right">$/MWh</th></tr></thead>
          <tbody>
            {lmps.map((r, i) => (
              <tr key={i} className="text-white"><td>{r.bus_id}</td><td className="text-right">{Number(r["lmp_$_per_MWh"] ?? r["lmp_p_$_per_MWh"] ?? 0).toFixed(2)}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
